/**
 * Privacy Policy Page
 * 
 * Legal document for NewAudia Systems, LLC
 * Version 1.1 | Effective Date: March 1, 2026
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { useAuth } from '../contexts/AuthContext';
import { Seo, PAGE_SEO } from '../components/Seo';

const PrivacyPolicyPage = () => {
  const { user } = useAuth();
  
  const getHomeRoute = () => {
    if (!user) return '/';
    const roles = user.roles || [];
    if (roles.includes('admin')) return '/admin';
    if (roles.includes('publisher')) return '/publisher';
    if (roles.includes('author') || roles.includes('creator')) return '/creator';
    if (roles.includes('reader')) return '/home';
    return '/';
  };
  
  const homeRoute = getHomeRoute();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <Seo {...PAGE_SEO.privacy} />
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={homeRoute} className="flex items-center gap-3">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-10 w-auto rounded-lg" />
          </Link>
          <Link 
            to={homeRoute}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Document Header */}
        <div className="mb-12 pb-8 border-b border-slate-700">
          <div className="flex items-center gap-4 mb-6">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-16 w-auto rounded-lg" />
            <div>
              <h1 data-testid="privacy-company-name" className="text-3xl font-bold text-white">NewAudia Systems, LLC</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-indigo-400" />
            <h2 data-testid="privacy-title" className="text-2xl font-semibold text-white">Privacy Policy</h2>
          </div>
          
          <div className="flex flex-wrap gap-6 text-sm text-slate-400">
            <p><span className="text-slate-500">Effective Date:</span> March 1, 2026</p>
            <p><span className="text-slate-500">Version:</span> 1.1</p>
            <p><span className="text-slate-500">Last Updated:</span> March 2026</p>
          </div>
        </div>

        {/* Policy Content */}
        <div className="prose prose-invert prose-slate max-w-none">
          {/* Important Notice */}
          <section className="mb-10">
            <p className="text-amber-400 font-semibold text-sm uppercase tracking-wide mb-4">Important — Please Read Carefully</p>
            <p className="text-slate-300 leading-relaxed">
              This Privacy Policy describes how NewAudia Systems, LLC collects, uses, discloses, protects, and retains 
              personal information when you access or use the NewAudia website, applications, and services (collectively, 
              the "Services"). By accessing or using the Services, you acknowledge that you have read, understood, and 
              agree to the practices described in this Privacy Policy.
            </p>
          </section>

          {/* Company Identification */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Company Identification</h3>
            <p className="text-slate-300 leading-relaxed">
              The Services are operated by <strong className="text-white">NewAudia Systems, LLC</strong>, a limited liability 
              company organized under the laws of the State of Colorado, with its principal place of business in Colorado Springs, 
              Colorado (hereinafter "NewAudia," "Company," "we," "us," or "our"). "NewAudia" is a registered trade name of 
              NewAudia Systems, LLC in the State of Colorado.
            </p>
            <p className="text-slate-300 leading-relaxed mt-4">
              This Privacy Policy is incorporated by reference into the NewAudia Terms of Service. For Authors and Publishers, 
              it is additionally subject to the NewAudia Author Publishing Agreement, Acceptable Use Policy, and AI Usage Policy. 
              Where those documents address specific data processing practices (such as Ephemeral Intelligence generation or AI 
              narration processing), this Privacy Policy supplements that disclosure with additional detail regarding collection, 
              retention, and user rights.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">1. Scope of This Policy</h3>
            <p className="text-slate-300 leading-relaxed mb-3">This Privacy Policy applies to:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Visitors to the NewAudia website</li>
              <li>Registered users, including Readers, Authors, Publishers, and Administrators</li>
              <li>Users of AI-assisted, interactive, and audiobook features, including the Audia AI reading assistant</li>
              <li>Authors and Publishers whose Manuscripts are processed through the NewAudia ingestion pipeline</li>
              <li>Communications with NewAudia via email, forms, or support channels</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              This Policy does not apply to third-party websites, platforms, or services that may be linked to or integrated 
              with the Services. Users should review the privacy policies of those third parties independently.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              We collect information that you provide directly and information that is automatically collected through 
              your use of the Services.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.1 Information You Provide Directly</h4>
            <p className="text-slate-300 leading-relaxed mb-3">This may include:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li><strong className="text-white">Account Information:</strong> Name, email address, username, password, and user role (Reader, Author, Publisher, or Administrator)</li>
              <li><strong className="text-white">Profile Information:</strong> Preferences, settings, and optional biographical or professional details</li>
              <li><strong className="text-white">Payment Information:</strong> Billing address, transaction records, and payment status. Payment card information is processed directly by Stripe, our third-party payment processor, and is not stored by NewAudia</li>
              <li><strong className="text-white">Manuscript and Content Submissions:</strong> Uploaded EPUB Manuscripts, cover images, metadata, author bios, and other materials submitted through the platform. See Section 4 for details on how Manuscript content is processed</li>
              <li><strong className="text-white">Communications:</strong> Support inquiries, feedback, survey responses, and other correspondence submitted to NewAudia</li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.2 Information Collected Automatically</h4>
            <p className="text-slate-300 leading-relaxed mb-3">This may include:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li><strong className="text-white">Usage Data:</strong> Feature interactions, audiobook listening activity, Audia query submissions, session duration, and navigation patterns</li>
              <li><strong className="text-white">Device and Technical Data:</strong> IP address, browser type, operating system, and device identifiers</li>
              <li><strong className="text-white">Log and Diagnostic Data:</strong> Error logs, performance metrics, and timestamps</li>
              <li><strong className="text-white">Cookies and Similar Technologies:</strong> As described in Section 11</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              We process personal information for legitimate business purposes, including to:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Provide, operate, and maintain the Services</li>
              <li>Create, authenticate, and manage user accounts</li>
              <li>Deliver audiobooks, AI-assisted features, and interactive content</li>
              <li>Process Manuscript ingestion, AI narration, and Audiobook publication</li>
              <li>Process transactions and manage billing via Stripe</li>
              <li>Generate and maintain Ephemeral Intelligence data to power the Audia AI reading assistant</li>
              <li>Improve performance, reliability, and usability of the Services</li>
              <li>Personalize content and user experience</li>
              <li>Communicate updates, notices, Commission reports, and support responses</li>
              <li>Detect, prevent, and mitigate fraud, abuse, or security incidents</li>
              <li>Comply with legal, regulatory, and contractual obligations</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4 font-medium">
              NewAudia does not sell personal information to third parties.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">4. AI-Assisted Processing and Data Flows</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              Certain Services rely on AI-assisted technologies. The following subsections describe how personal data and 
              user content are handled in connection with those features. For a full description of how AI features work, 
              see the NewAudia AI Usage Policy.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.1 Author Manuscript Processing</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              When an Author or Publisher submits a Manuscript through the ingestion pipeline, the Manuscript text is 
              transmitted to OpenAI's Text-to-Speech (TTS) API to generate AI-narrated audio. This transmission is governed 
              by NewAudia's agreement with OpenAI. NewAudia does not authorize OpenAI to use Manuscript content to train 
              general-purpose AI models under the terms of that agreement.
            </p>
            <p className="text-slate-300 leading-relaxed mb-3">
              Additionally, NewAudia generates "Ephemeral Intelligence" data from each ingested Manuscript — consisting of 
              AI-generated indexing, segmentation metadata, and conversational context data — used exclusively to power the 
              Audia AI reading assistant for that title. Ephemeral Intelligence is not shared with third parties for commercial 
              purposes beyond what is necessary to operate the platform.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Authors who remove a Manuscript or terminate their Author Publishing Agreement may request deletion of associated 
              Audiobook files. Ephemeral Intelligence data and associated metadata may be retained by NewAudia for a reasonable 
              period following removal for archival, legal, or compliance purposes, consistent with Section 7.2 of the Author 
              Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.2 Reader Query Processing (Audia)</h4>
            <p className="text-slate-300 leading-relaxed">
              When a Reader submits a question to Audia, that query — along with relevant context retrieved from the 
              Manuscript's Ephemeral Intelligence index — is transmitted to OpenAI's language model API to generate a 
              response. Reader queries are not stored for the purpose of training generalized AI models. Query data may 
              be retained in platform logs for quality assurance, abuse prevention, and system improvement purposes.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.3 General AI Output Limitations</h4>
            <p className="text-slate-300 leading-relaxed">
              AI outputs are generated algorithmically and do not constitute professional, legal, financial, or medical 
              advice. Users are responsible for ensuring they have the necessary rights to submit any content they provide 
              through the Services.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">5. Information Sharing and Disclosure</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              We share information only as necessary and under appropriate safeguards. We do not sell personal information.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.1 Service Providers</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              We share information with trusted service providers that perform services on our behalf. Material third-party 
              processors currently engaged include:
            </p>
            <div className="overflow-x-auto mb-4">
              <table data-testid="privacy-provider-table" className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-44">Provider</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Purpose</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">OpenAI</td>
                    <td className="py-3 px-4 text-slate-400">AI narration (Text-to-Speech API) for Audiobook production; language model API for Audia AI reading assistant query processing.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">Stripe</td>
                    <td className="py-3 px-4 text-slate-400">Payment processing, billing, and Commission disbursements to Authors and Publishers.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">Cloudflare R2</td>
                    <td className="py-3 px-4 text-slate-400">Cloud storage for Audiobook audio files and Manuscript assets.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">MongoDB Atlas</td>
                    <td className="py-3 px-4 text-slate-400">Database services for platform data, user accounts, and Manuscript metadata.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">Email / SMTP Provider</td>
                    <td className="py-3 px-4 text-slate-400">Transactional email delivery for account notifications, Commission reports, and system communications.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-slate-300 leading-relaxed">
              These providers are contractually required to protect information and use it only for authorized purposes. 
              NewAudia will update this Policy if its material third-party provider relationships change.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.2 Legal and Compliance Obligations</h4>
            <p className="text-slate-300 leading-relaxed mb-3">We may disclose information where required to:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Comply with applicable law, regulation, or lawful legal process</li>
              <li>Enforce our Terms of Service, Author Publishing Agreement, Acceptable Use Policy, or other platform policies</li>
              <li>Protect the rights, property, or safety of NewAudia, our users, or others</li>
              <li>Respond to copyright infringement claims or other third-party legal notices relating to Manuscripts published on the platform</li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.3 Business Transfers</h4>
            <p className="text-slate-300 leading-relaxed">
              If NewAudia undergoes a merger, reorganization, asset sale, or transfer of ownership, personal information 
              and platform data may be transferred as part of that transaction. Users will be notified of any such transfer 
              and of any material changes to this Privacy Policy that result from it, as required by applicable law.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">6. Data Retention</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              Personal information is retained only for as long as reasonably necessary to fulfill the purposes described 
              in this Policy, meet legal or regulatory requirements, resolve disputes, and enforce agreements. Specific 
              considerations include:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Account data is retained for the duration of the account and for a reasonable period thereafter to comply with legal obligations or resolve outstanding matters</li>
              <li>Manuscript and Audiobook files are removed from public access within thirty (30) business days following a valid termination or removal request, consistent with Section 7.2 of the Author Publishing Agreement</li>
              <li>Ephemeral Intelligence data and associated Manuscript metadata may be retained for archival, legal, or compliance purposes following Manuscript removal, as permitted under the Author Publishing Agreement</li>
              <li>Audia query log data is retained for quality assurance, abuse prevention, and platform improvement, subject to applicable legal requirements</li>
              <li>Payment and transaction records are retained as required by applicable tax, accounting, and financial regulations</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">7. Data Security</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              NewAudia employs reasonable administrative, technical, and organizational safeguards designed to protect personal 
              information from unauthorized access, disclosure, alteration, or destruction. These measures include access controls, 
              encrypted data transmission, and routine security reviews.
            </p>
            <p className="text-slate-300 leading-relaxed">
              No system is completely secure. While we take appropriate precautions, we cannot guarantee absolute security of 
              information transmitted to or stored on the Services. Users are responsible for maintaining the confidentiality 
              of their account credentials.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">8. User Rights and Choices</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              Depending on your jurisdiction, you may have rights with respect to your personal information, including the right to:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Access and review the personal information we hold about you</li>
              <li>Request correction of inaccurate or incomplete information</li>
              <li>Request deletion of your personal information, subject to our legal obligations and retention requirements</li>
              <li>Object to or request restriction of certain processing activities</li>
              <li>Withdraw consent where processing is based on consent, without affecting the lawfulness of prior processing</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              To exercise any of these rights, please submit a request using the contact information in Section 13. NewAudia 
              may require identity verification before processing any request. Some requests may be limited by applicable law 
              or by our obligations under the Author Publishing Agreement or other contracts.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">9. Author Rights Regarding Manuscript Data</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              Authors and Publishers have specific rights regarding their Manuscripts and the data derived from them, governed 
              primarily by the Author Publishing Agreement. The following summarizes the key data rights in that context:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Authors retain full copyright and ownership of their Manuscripts. NewAudia holds only a non-exclusive license to process and distribute content as described in Article 4 of the Author Publishing Agreement</li>
              <li>Authors may request removal of their Manuscripts and associated Audiobooks at any time by contacting <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">admin@newaudia.com</a>. Removal is processed within thirty (30) business days</li>
              <li>Ephemeral Intelligence data derived from a Manuscript may be retained after removal for archival and legal compliance purposes, as permitted under Section 7.2 of the Author Publishing Agreement</li>
              <li>Authors may submit data access or correction requests for their account information using the contact details in Section 13</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">10. Children's Privacy</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              The Services are intended for adults only. Per Section 1.1 of the NewAudia Author & Publisher Addendum, Authors 
              and Publishers must be at least 18 years of age or the age of majority in their jurisdiction. Reader accounts are 
              similarly restricted to users who are 18 or older.
            </p>
            <p className="text-slate-300 leading-relaxed">
              NewAudia does not knowingly collect personal information from individuals under 18 years of age. If we become aware 
              that personal information has been collected from a minor without verifiable parental consent, we will take steps to 
              delete that information promptly. If you believe a minor has provided personal information through the Services, 
              please contact us at{' '}
              <a href="mailto:privacy@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">privacy@newaudia.com</a>.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">11. Cookies and Tracking Technologies</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              We use cookies and similar technologies to enable core site functionality, analyze usage and performance, and 
              remember user preferences. Cookies used on the Services are limited to those necessary for platform operation 
              and analytics.
            </p>
            <p className="text-slate-300 leading-relaxed">
              You may control or disable cookies through your browser settings. Please note that disabling certain cookies may 
              affect the functionality of some features of the Services.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">12. International Data Processing</h3>
            <p className="text-slate-300 leading-relaxed">
              NewAudia is based in Colorado, USA. Information collected through the Services may be processed and stored in 
              the United States or in other jurisdictions where our service providers operate. By using the Services, you 
              consent to the transfer of your information to jurisdictions that may have data protection rules different from 
              those in your country of residence, subject to the safeguards described in this Policy.
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">13. Contact Information</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              For questions, concerns, or privacy requests — including data access, correction, deletion, or rights inquiries 
              — please contact NewAudia using the information below.
            </p>
            <div data-testid="privacy-contact-info" className="bg-slate-900/50 border border-slate-700 rounded-lg p-6">
              <p className="text-white font-semibold text-lg">NewAudia Systems, LLC</p>
              <p className="text-slate-400 text-sm">A Colorado Limited Liability Company</p>
              <p className="text-slate-400 text-sm">"NewAudia" is a registered trade name in Colorado</p>
              <p className="text-slate-400 mt-2">Colorado Springs, Colorado, USA</p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400 text-sm">Privacy Requests & Data Inquiries:</span>
                  <a href="mailto:privacy@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm">
                    privacy@newaudia.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400 text-sm">General Inquiries:</span>
                  <a href="mailto:info@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm">
                    info@newaudia.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400 text-sm">Administrative & Legal Notices:</span>
                  <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm">
                    admin@newaudia.com
                  </a>
                </div>
              </div>
              <p className="text-slate-400 text-sm mt-3">
                Website: <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>
              </p>
            </div>
          </section>

          {/* Section 14 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">14. Changes to This Privacy Policy</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              NewAudia may update this Privacy Policy periodically to reflect changes in our practices, legal requirements, 
              or platform features. Updates will be posted on the Services with a revised "Last Updated" date. NewAudia will 
              use reasonable efforts to notify users of material changes via email to the address on file. Continued use of 
              the Services following notice of any update constitutes acceptance of the revised Policy.
            </p>

            <h4 className="text-lg font-medium text-white mb-3">Version History</h4>
            <div className="overflow-x-auto">
              <table data-testid="privacy-version-history" className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-20">Version</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-36">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Summary of Changes</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-slate-300">1.0</td>
                    <td className="py-3 px-4 text-slate-400">January 1, 2026</td>
                    <td className="py-3 px-4 text-slate-400">Initial publication. Entity listed as NewAudia dba Use What You Got Publishing, LLC. General privacy disclosures; entity transition language noting future migration to NewAudia Systems, LLC.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">1.1</td>
                    <td className="py-3 px-4 text-slate-300">March 2026</td>
                    <td className="py-3 px-4 text-slate-400">
                      Entity updated to NewAudia Systems, LLC (registered trade name in Colorado). Removed dba and entity 
                      transition language. Added Company Identification section; expanded scope to include Manuscript submissions 
                      and Audia queries; named third-party providers (OpenAI, Stripe, Cloudflare R2, MongoDB Atlas); added 
                      AI-assisted processing section with Author-specific (4.1) and Reader-specific (4.2) data flow disclosures; 
                      added Ephemeral Intelligence and Audia query retention details; updated data retention to align with Author 
                      Publishing Agreement Section 7.2; updated children's privacy minimum age to 18 per Addendum Section 1.1; 
                      added Author rights section (Section 9); updated contact block with privacy@newaudia.com; added version 
                      history table.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Footer Entity Statement */}
          <section className="mt-12 pt-8 border-t border-slate-700">
            <p className="text-sm text-slate-500">
              NewAudia Systems, LLC | Colorado Springs, Colorado |{' '}
              <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>
              {' '}| <a href="mailto:privacy@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">privacy@newaudia.com</a>
            </p>
            <p className="text-sm text-slate-500 mt-2 italic">
              "NewAudia" is a registered trade name of NewAudia Systems, LLC in the State of Colorado.
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Version 1.1 — March 2026
            </p>
          </section>
        </div>
      </main>

      <UniversalFooter />
    </div>
  );
};

export default PrivacyPolicyPage;
